import { useEffect, useMemo, useState } from 'react'
import { Radio, Plus, Trash2, Edit3, Upload, Star, Users, Music2, Award, Play, Inbox, Check, X as XIcon, Lock, Unlock, Vote, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog'
import { useConfirm } from '../hooks/useConfirm'
import { visionAPI } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import IconPicker from '@/components/IconPicker'
import TeamMark, { parseFlag } from '@/components/TeamMark'
import KempenvisionNav from '@/components/KempenvisionNav'

/* ================================================================
   Kempenvision Admin — superadmin CRUD for shows, teams, entries, juries
================================================================ */

const DEFAULT_FLAG = ['#ff2b8f', '#0b0518', '#00d9ff']

export default function KempenvisionAdminPage() {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { user } = useAuth()
  const isSuperadmin = user?.role === 'superadmin' || user?.role === 'admin'

  const [shows, setShows]     = useState([])
  const [currentShowId, setCurrentShowId] = useState(null)
  const [genres, setGenres]   = useState([])
  const [users, setUsers]     = useState([])
  const [loading, setLoading] = useState(true)
  // Tab-state met deeplink-sync (?tab=uitreiking) zodat Regie direct kan
  // linken naar de uitreiking-tab.
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTabState] = useState(() => searchParams.get('tab') || 'show')
  const setTab = (v) => {
    setTabState(v)
    const p = new URLSearchParams(searchParams)
    if (v && v !== 'show') p.set('tab', v); else p.delete('tab')
    setSearchParams(p, { replace: true })
  }

  const currentShow = shows.find(s => s.id === currentShowId)

  const reloadShows = async () => {
    const { data } = await visionAPI.listShows()
    setShows(data.shows)
    if (!data.shows.find(s => s.id === currentShowId)) {
      const active = data.shows.find(s => s.is_active) ?? data.shows[0]
      setCurrentShowId(active?.id ?? null)
    }
  }

  useEffect(() => {
    if (!isSuperadmin) return
    ;(async () => {
      try {
        const [showsR, genresR, usersR] = await Promise.all([
          visionAPI.listShows(),
          visionAPI.getGenres(),
          visionAPI.listEligibleUsers(),
        ])
        setShows(showsR.data.shows)
        setGenres(genresR.data.genres)
        setUsers(usersR.data.users)
        const active = showsR.data.shows.find(s => s.is_active) ?? showsR.data.shows[0]
        setCurrentShowId(active?.id ?? null)
      } catch (e) { console.error(e) } finally { setLoading(false) }
    })()
  }, [isSuperadmin])

  if (!isSuperadmin) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Enkel superadmins hebben toegang tot Kempenvision beheer.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <KempenvisionNav />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-[4px] border border-border bg-card flex items-center justify-center" style={{ borderLeft: "2px solid rgb(var(--acid-rgb))" }}>
            <Radio className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Kempenvision admin</h1>
            <p className="text-sm text-muted-foreground">Beheer shows, teams, entries en jury's</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => {
            if (window.history.length > 1) navigate(-1); else navigate('/')
          }} title="Vorige pagina (browser-history)">
            <ArrowLeft className="h-4 w-4 mr-1" />Vorige
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/kempenvision">← Overzicht</Link>
          </Button>
        </div>
      </div>

      <ShowsBar
        shows={shows}
        currentShowId={currentShowId}
        setCurrentShowId={setCurrentShowId}
        onChanged={reloadShows}
      />

      {currentShow && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="show"><Star className="h-4 w-4 mr-2" />Show</TabsTrigger>
            <TabsTrigger value="submissions"><Inbox className="h-4 w-4 mr-2" />Inschrijvingen</TabsTrigger>
            <TabsTrigger value="teams"><Users className="h-4 w-4 mr-2" />Teams</TabsTrigger>
            <TabsTrigger value="entries"><Music2 className="h-4 w-4 mr-2" />Entries</TabsTrigger>
            <TabsTrigger value="juries"><Award className="h-4 w-4 mr-2" />Jury's</TabsTrigger>
            <TabsTrigger value="voting"><Vote className="h-4 w-4 mr-2" />Jury punten</TabsTrigger>
            {currentShow.public_scoring_enabled && currentShow.public_scoring_mode === "admin_input" && <TabsTrigger value="public"><Vote className="h-4 w-4 mr-2" />Publiek punten</TabsTrigger>}
            <TabsTrigger value="uitreiking">
              <Award className="h-4 w-4 mr-2" />Uitreiking
              {!currentShow.results_awarded_at && <span className="ml-2 h-1.5 w-1.5 rounded-full bg-peach" />}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="show" className="mt-4">
            <ShowTab show={currentShow} onSaved={reloadShows} />
          </TabsContent>
          <TabsContent value="submissions" className="mt-4">
            <SubmissionsTab showId={currentShowId} />
          </TabsContent>
          <TabsContent value="teams" className="mt-4">
            <TeamsTab showId={currentShowId} />
          </TabsContent>
          <TabsContent value="entries" className="mt-4">
            <EntriesTab showId={currentShowId} genres={genres} />
          </TabsContent>
          <TabsContent value="juries" className="mt-4">
            <JuriesTab showId={currentShowId} users={users} />
          </TabsContent>
          <TabsContent value="voting" className="mt-4">
            <AdminJuryVotingTab showId={currentShowId} />
          </TabsContent>
          <TabsContent value="public" className="mt-4">
            <AdminPublicScoringTab />
          </TabsContent>
          <TabsContent value="uitreiking" className="mt-4">
            <UitreikingTab show={currentShow} onDone={reloadShows} />
          </TabsContent>
        </Tabs>
      )}

      {!currentShow && (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            Nog geen show. Klik "Nieuwe show" hierboven om er één aan te maken.
          </CardContent>
        </Card>
      )}
    </div>
  )
}

/* =============================================================
   Shows bar — selector + create + activate
============================================================= */
function ShowsBar({ shows, currentShowId, setCurrentShowId, onChanged }) {
  const [dialog, setDialog] = useState(false)
  const [name, setName]     = useState('')
  const [edition, setEdition] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [regType, setRegType] = useState('team')   // 'team' of 'user'

  const create = async (e) => {
    e.preventDefault()
    const { data } = await visionAPI.createShow({ name: name.trim(), edition, subtitle, registration_type: regType })
    setDialog(false); setName(''); setEdition(''); setSubtitle(''); setRegType('team')
    await onChanged()
    if (data.id) setCurrentShowId(data.id)
  }

  const activate = async (id) => {
    await visionAPI.activateShow(id)
    await onChanged()
  }

  const del = async (id) => {
    if (!(await confirm('Show verwijderen? Alle teams, entries, jury\'s en stemmen worden verwijderd.'))) return
    await visionAPI.deleteShow(id)
    await onChanged()
  }
  const reset = async (id) => {
    if (!(await confirm('Show leegmaken? Teams, entries, jury\'s, submissions en stemmen worden gewist. De show zelf blijft bestaan.'))) return
    await visionAPI.resetShow(id)
    await onChanged()
    alert('Show geleegd.')
  }
  const populate = async (id) => {
    if (!(await confirm('Populate show met mock data? Voegt 6 teams + 6 entries + 3 jury\'s toe. Doe dit best op een lege show.'))) return
    const r = await visionAPI.populateShowMock(id)
    await onChanged()
    alert(r.data?.message ?? 'Populate klaar')
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Label className="shrink-0">Actieve editie:</Label>
          <Select value={currentShowId ? String(currentShowId) : ''} onValueChange={(v) => setCurrentShowId(Number(v))}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Kies show…" /></SelectTrigger>
            <SelectContent>
              {shows.map(s => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}{s.edition ? ` — ${s.edition}` : ''}{s.is_active ? ' ★' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {shows.find(s => s.id === currentShowId) && !shows.find(s => s.id === currentShowId)?.is_active && (
            <Button variant="secondary" size="sm" onClick={() => activate(currentShowId)}>
              <Star className="h-4 w-4 mr-1" /> Activeer
            </Button>
          )}
          {currentShowId && (
            <>
              <Button variant="outline" size="sm" onClick={() => populate(currentShowId)}
                      title="Mock data toevoegen voor testen">
                <Play className="h-4 w-4 mr-1" /> Mock data
              </Button>
              <Button variant="outline" size="sm" onClick={() => reset(currentShowId)}
                      title="Wist alle teams/entries/jury's/stemmen — show blijft">
                <RefreshCw className="h-4 w-4 mr-1" /> Leegmaken
              </Button>
              <Button variant="ghost" size="sm" onClick={() => del(currentShowId)}
                      title="Show volledig verwijderen">
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </>
          )}
          <div className="flex-1" />
          <Button onClick={() => setDialog(true)}>
            <Plus className="h-4 w-4 mr-1" /> Nieuwe show
          </Button>
        </div>
      </CardContent>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nieuwe show</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><Label>Naam</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Kempenvision" required /></div>
            <div><Label>Editie</Label><Input value={edition} onChange={e => setEdition(e.target.value)} placeholder="2026" /></div>
            <div><Label>Subtitel</Label><Input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="The Basement Sessions" /></div>
            <div>
              <Label>Inschrijvingsformaat</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setRegType('team')}
                        className={`rounded-md border p-3 text-left transition-colors ${
                          regType === 'team' ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}>
                  <div className="text-sm font-semibold">Teams</div>
                  <div className="text-xs text-muted-foreground mt-1">Elke entrant is een team uit de Teams-module.</div>
                </button>
                <button type="button" onClick={() => setRegType('user')}
                        className={`rounded-md border p-3 text-left transition-colors ${
                          regType === 'user' ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}>
                  <div className="text-sm font-semibold">Users</div>
                  <div className="text-xs text-muted-foreground mt-1">Elke entrant is een user met vrij te kiezen artiestennaam.</div>
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialog(false)}>Annuleer</Button>
              <Button type="submit">Aanmaken</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

/* =============================================================
   Show tab — edit metadata + phase
============================================================= */
const PHASES = [
  { key: 'setup',          label: 'Setup' },
  { key: 'live_songs',     label: 'Live songs' },
  { key: 'jury_reveal',    label: 'Jury reveal' },
  { key: 'public_vote',    label: 'Publiek stemt' },
  { key: 'public_reveal',  label: 'Publiek reveal' },
  { key: 'finished',       label: 'Klaar' },
]

function ShowTab({ show, onSaved }) {
  const [name, setName]         = useState(show.name)
  const [edition, setEdition]   = useState(show.edition ?? '')
  const [subtitle, setSubtitle] = useState(show.subtitle ?? '')
  const [rulesText, setRulesText]   = useState(show.rules_text || '')
  const [voteOpen, setVoteOpen]     = useState(!!show.vote_open)
  const [pscoreOn, setPscoreOn]     = useState(show.public_scoring_enabled !== false)
  const [pscoreMode, setPscoreMode] = useState(show.public_scoring_mode ?? 'vote')
  const [subOpen, setSubOpen]       = useState(!!show.submissions_open)
  const [badgeId, setBadgeId]       = useState(show.winner_badge_type_id ?? '')
  const [badges, setBadges]         = useState([])
  const [saving, setSaving]     = useState(false)
  const [awarding, setAwarding] = useState(false)
  const [awardMsg, setAwardMsg] = useState(null)

  useEffect(() => {
    setName(show.name); setEdition(show.edition ?? ''); setSubtitle(show.subtitle ?? '')
    setRulesText(show.rules_text || ''); setVoteOpen(!!show.vote_open); setPscoreOn(show.public_scoring_enabled !== false); setPscoreMode(show.public_scoring_mode ?? 'vote')
    setSubOpen(!!show.submissions_open)
    setBadgeId(show.winner_badge_type_id ?? '')
    setAwardMsg(null)
  }, [show.id, show.winner_badge_type_id])

  useEffect(() => {
    import('@/lib/api').then(({ badgeAPI }) => {
      badgeAPI.getAllTypes().then(r => setBadges(r.data?.badges ?? [])).catch(() => {})
    })
  }, [])

  const save = async () => {
    setSaving(true)
    try {
      await visionAPI.updateShow(show.id, {
        name, edition, subtitle, submissions_open: subOpen, rules_text: rulesText,
        vote_open: voteOpen,
        public_scoring_enabled: pscoreOn,
        public_scoring_mode: pscoreMode,
        winner_badge_type_id: badgeId || null,
      })
      await onSaved()
    } finally { setSaving(false) }
  }

  const award = async () => {
    if (!confirm('Uitreiking: registreert de top-5 in het compo-overzicht en geeft (indien gekozen) de winnaar-badge aan de deelnemende user(s). Dit is een handmatige actie die maar één keer per show werkt. Doorgaan?')) return
    setAwarding(true); setAwardMsg(null)
    try {
      await visionAPI.awardResults(show.id)
      setAwardMsg('Uitreiking uitgevoerd')
      await onSaved()
    } catch (e) {
      setAwardMsg(e.response?.data?.error ?? e.message)
    } finally { setAwarding(false) }
  }

  const toggleSubmissions = async () => {
    const next = !subOpen
    setSubOpen(next); setSaving(true)
    try { await visionAPI.updateShow(show.id, { submissions_open: next }); await onSaved() }
    finally { setSaving(false) }
  }

  const togglePscoreOn = async () => {
    const next = !pscoreOn; setPscoreOn(next); setSaving(true)
    try { await visionAPI.updateShow(show.id, { public_scoring_enabled: next }); await onSaved() }
    finally { setSaving(false) }
  }
  const changePscoreMode = async (m) => {
    setPscoreMode(m); setSaving(true)
    try { await visionAPI.updateShow(show.id, { public_scoring_mode: m }); await onSaved() }
    finally { setSaving(false) }
  }
  const toggleVoteOpen = async () => {
    const next = !voteOpen; setVoteOpen(next); setSaving(true)
    try { await visionAPI.updateShow(show.id, { vote_open: next }); await onSaved() }
    finally { setSaving(false) }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Show details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Naam</Label><Input value={name} onChange={e => setName(e.target.value)} /></div>
            <div><Label>Editie</Label><Input value={edition} onChange={e => setEdition(e.target.value)} /></div>
            <div className="md:col-span-2"><Label>Subtitel</Label><Input value={subtitle} onChange={e => setSubtitle(e.target.value)} /></div>

          </div>
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button>
            <Badge variant={show.is_active ? 'default' : 'outline'}>
              {show.is_active ? '★ Actief' : 'Niet actief'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Winnaar-badge</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Winnaar-badge (optioneel)</Label>
            <select value={badgeId ?? ''}
                    onChange={e => setBadgeId(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              <option value="">— Geen automatische badge —</option>
              {badges.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Beheer badges op <a href="/admin/badges" className="text-acid hover:underline">/admin/badges</a>.
              Het uitreiken zelf gebeurt in de <strong>Uitreiking</strong>-tab.
            </p>
          </div>
          {/* Award-knop weggehaald: was dubbel met de Uitreiking-tab. Configureer
              badge hier, reik uit in de Uitreiking-tab. Status blijft daar zichtbaar. */}
          {awardMsg && <p className="text-sm text-muted-foreground">{awardMsg}</p>}
          <Button className="mt-2" onClick={save} disabled={saving}>Instellingen opslaan</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Regels & uitleg</CardTitle></CardHeader>
        <CardContent>
          <textarea
            value={rulesText}
            onChange={e => setRulesText(e.target.value)}
            rows={6}
            className="w-full rounded-[4px] border border-border bg-background px-3 py-2 text-sm font-mono"
            placeholder="Beschrijf hoe deze compo werkt: verloop, regels, deadlines, prijzen. Deelnemers zien dit op de Kempenvision hub." />
          <Button className="mt-2" onClick={save} disabled={saving}>Opslaan</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Publiekspunten</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-semibold">
                {pscoreOn
                  ? <span className="text-emerald-400">Aan — publiek punten meetellen in de show</span>
                  : <span className="text-muted-foreground">Uit — alleen jury telt</span>}
              </div>
              <div className="text-sm text-muted-foreground">Bepaalt of publieke reveal + publieks-tab zichtbaar zijn.</div>
            </div>
            <Button variant={pscoreOn ? 'default' : 'outline'} onClick={togglePscoreOn} disabled={saving}>
              {pscoreOn ? 'Aan' : 'Uit'}
            </Button>
          </div>
          {pscoreOn && (
            <>
              <div className="flex items-center gap-3">
                <Label className="w-40 mb-0">Wie voert punten in</Label>
                <Select value={pscoreMode} onValueChange={changePscoreMode} disabled={saving}>
                  <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vote">Publiek stemt via mobiel</SelectItem>
                    <SelectItem value="admin_input">Admin voert punten in</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {pscoreMode === 'vote' && (
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-semibold">
                      {voteOpen
                        ? <span className="text-emerald-400">Publiek stemmen OPEN</span>
                        : <span className="text-yellow-500">Publiek stemmen dicht</span>}
                    </div>
                    <div className="text-sm text-muted-foreground">Zet aan zodra publiek mag stemmen via <code>/kempenvision/vote</code>.</div>
                  </div>
                  <Button variant={voteOpen ? 'ghost' : 'default'} onClick={toggleVoteOpen} disabled={saving}>
                    {voteOpen ? 'Sluiten' : 'Openen'}
                  </Button>
                </div>
              )}
              {pscoreMode === 'admin_input' && (
                <div className="text-sm text-muted-foreground">
                  Publieke stem-pagina wordt uitgeschakeld. Voer publiek punten in via het tabblad <b>Publiek punten</b>.
                </div>
              )}
            </>
          )}
          <div className="pt-2 text-xs text-muted-foreground border-t border-border">
            De show-fase wordt automatisch afgeleid uit welke view actief is (idle/song/ceremony/results). Geen manuele fase meer nodig.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Inschrijvingen</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-semibold">
                {subOpen
                  ? <span className="text-emerald-400">Open — deelnemers kunnen submitten</span>
                  : <span className="text-yellow-500">Gesloten — geen nieuwe inzendingen</span>}
              </div>
              <div className="text-sm text-muted-foreground">
                Bepaalt of users op <code>/kempenvision/inleveren</code> hun MP3 kunnen indienen. Reeds ingediende submissions blijven zichtbaar.
              </div>
            </div>
            <Button variant={subOpen ? 'ghost' : 'default'} onClick={toggleSubmissions} disabled={saving}>
              {subOpen ? <><Lock className="h-4 w-4 mr-2" />Sluiten</> : <><Unlock className="h-4 w-4 mr-2" />Openen</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/* =============================================================
   Teams tab
============================================================= */
function TeamsTab({ showId }) {
  const [teams, setTeams]           = useState([])
  const [show, setShow]             = useState(null)
  const [persistentTeams, setPersistentTeams] = useState([])
  const [users, setUsers]           = useState([])
  const [dialog, setDialog] = useState(null)

  const reload = async () => {
    const { data } = await visionAPI.listTeams(showId)
    setTeams(data.teams.map(t => ({ ...t, flag_colors: parseFlag(t.flag_colors) })))
    const s = await visionAPI.listShows()
    setShow(s.data?.shows?.find(x => x.id === showId) ?? null)
  }
  useEffect(() => {
    reload()
    // Voor de picker-dropdowns: teams (voor team-mode) en users (voor user-mode).
    import('@/lib/api').then(({ teamsAPI, playersAPI }) => {
      teamsAPI.list().then(r => setPersistentTeams(r.data?.teams ?? [])).catch(() => {})
      playersAPI.list('').then(r => setUsers(r.data?.players ?? r.data?.users ?? [])).catch(() => {})
    })
  }, [showId])

  const regType = show?.registration_type || 'team'

  const openNew  = () => setDialog({ name: '', code: '', icon_name: null, icon_color: '#B4FF39',
                                     mode: 'icon', useLogo: false, logoFile: null,
                                     persistent_team_id: '', user_id: '' })
  const openEdit = (t) => setDialog({
    id: t.id, name: t.name, code: t.code ?? '',
    icon_name: t.icon_name ?? null,
    icon_color: t.icon_color ?? '#B4FF39',
    mode: t.logo_url ? 'logo' : 'icon',
    useLogo: !!t.logo_url, logoFile: null, existingLogo: t.logo_url,
    persistent_team_id: t.persistent_team_id ?? '',
    user_id: t.user_id ?? '',
  })

  const save = async (e) => {
    e.preventDefault()
    const payload = {
      name: dialog.name.trim(), code: dialog.code || null,
      flag_colors: null,
      icon_name: dialog.mode === 'icon' ? dialog.icon_name : null,
      icon_color: dialog.mode === 'icon' ? dialog.icon_color : null,
      persistent_team_id: regType === 'team' && dialog.persistent_team_id ? Number(dialog.persistent_team_id) : null,
      user_id:            regType === 'user' && dialog.user_id ? Number(dialog.user_id) : null,
    }
    let id = dialog.id
    if (id) {
      await visionAPI.updateTeam(id, payload)
    } else {
      const { data } = await visionAPI.createTeam(showId, payload)
      id = data.id
    }
    if (dialog.mode === 'logo' && dialog.logoFile) {
      await visionAPI.uploadTeamLogo(id, dialog.logoFile)
    }
    setDialog(null); await reload()
  }

  const del = async (id) => {
    if (!(await confirm('Team verwijderen? Alle bijbehorende entries vervallen ook.'))) return
    await visionAPI.deleteTeam(id); await reload()
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Teams ({teams.length})</CardTitle>
        <Button onClick={openNew} size="sm"><Plus className="h-4 w-4 mr-1" /> Nieuw team</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {teams.length === 0 && <p className="text-sm text-muted-foreground">Nog geen teams. Voeg er een toe om aan de slag te gaan.</p>}
        {teams.map(t => (
          <div key={t.id} className="flex items-center gap-3 p-3 rounded border border-border bg-card">
            <TeamMark {...t} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{t.name}</div>
              <div className="text-xs text-muted-foreground">{t.code || '—'}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => openEdit(t)}><Edit3 className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => del(t.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        ))}
      </CardContent>

      {dialog && (
        <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{dialog.id ? 'Team bewerken' : 'Nieuw team'}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Inschrijvingsformaat: <span className="text-acid">{regType === 'user' ? 'users (artiestennaam)' : 'teams'}</span>
              </div>

              {regType === 'team' && (
                <div>
                  <Label>Persistent team (optioneel)</Label>
                  <select value={dialog.persistent_team_id ?? ''}
                          onChange={e => {
                            const id = e.target.value
                            const t = persistentTeams.find(x => String(x.id) === id)
                            setDialog({ ...dialog, persistent_team_id: id,
                              name: t?.name ?? dialog.name,
                            })
                          }}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                    <option value="">— Los team, geen link —</option>
                    {persistentTeams.map(t => (
                      <option key={t.id} value={t.id}>{t.name} · {t.members_count} leden</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Als je koppelt aan een persistent team, weet het systeem later welke leden dit team vertegenwoordigen.
                  </p>
                </div>
              )}

              {regType === 'user' && (
                <div>
                  <Label>User (deelnemer)</Label>
                  <select value={dialog.user_id ?? ''}
                          onChange={e => setDialog({ ...dialog, user_id: e.target.value })}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                    <option value="">— Kies user —</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.display_name || u.username} (@{u.username})</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label>{regType === 'user' ? 'Artiestennaam' : 'Naam'}</Label>
                <Input value={dialog.name} onChange={e => setDialog({ ...dialog, name: e.target.value })} required
                       placeholder={regType === 'user' ? 'Bijv. "DJ Basement"' : 'Team Basement'} />
              </div>
              <div><Label>Code (3 letters)</Label><Input value={dialog.code} onChange={e => setDialog({ ...dialog, code: e.target.value.toUpperCase().slice(0,3) })} placeholder="BSM" /></div>
              <div className="flex items-center gap-4 pt-2 flex-wrap">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={dialog.mode === 'icon'} onChange={() => setDialog({ ...dialog, mode: 'icon', useLogo: false })} />
                  Icoon kiezen
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={dialog.mode === 'logo'} onChange={() => setDialog({ ...dialog, mode: 'logo', useLogo: true })} />
                  Logo uploaden
                </label>
              </div>
              {dialog.mode === 'icon' && (
                <IconPicker
                  value={{ icon_name: dialog.icon_name, icon_color: dialog.icon_color }}
                  onChange={v => setDialog({ ...dialog, icon_name: v.icon_name, icon_color: v.icon_color })}
                />
              )}
                            {dialog.mode === 'logo' && (
                <div>
                  <Label>Logo (max 2MB)</Label>
                  <Input type="file" accept="image/*" onChange={e => setDialog({ ...dialog, logoFile: e.target.files[0] })} />
                  {dialog.existingLogo && !dialog.logoFile && (
                    <img src={dialog.existingLogo} alt="" className="h-16 w-16 object-contain mt-2 rounded bg-muted" />
                  )}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialog(null)}>Annuleer</Button>
                <Button type="submit">{dialog.id ? 'Opslaan' : 'Aanmaken'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  )
}



/* =============================================================
   Entries tab
============================================================= */
function EntriesTab({ showId, genres }) {
  const [teams, setTeams] = useState([])
  const [entries, setEntries] = useState([])
  const [dialog, setDialog] = useState(null)

  const reload = async () => {
    const [tR, eR] = await Promise.all([visionAPI.listTeams(showId), visionAPI.listEntries(showId)])
    setTeams(tR.data.teams.map(t => ({ ...t, flag_colors: parseFlag(t.flag_colors) })))
    setEntries(eR.data.entries.map(e => ({ ...e, team_flag_colors: parseFlag(e.team_flag_colors) })))
  }
  useEffect(() => { reload() }, [showId])

  const openNew = () => setDialog({
    team_id: teams[0]?.id ?? '', title: '', artist_label: '', genre: genres[0]?.key ?? 'edm',
    bpm: '', running_order: entries.length + 1, audioFile: null,
  })
  const openEdit = (e) => setDialog({
    id: e.id, team_id: e.team_id, title: e.title, artist_label: e.artist_label ?? '',
    genre: e.genre, bpm: e.bpm ?? '', running_order: e.running_order,
    audioFile: null, existingAudio: e.audio_url,
  })

  const save = async (ev) => {
    ev.preventDefault()
    const payload = {
      team_id: Number(dialog.team_id), title: dialog.title.trim(),
      artist_label: dialog.artist_label || null, genre: dialog.genre,
      bpm: dialog.bpm ? Number(dialog.bpm) : null,
      running_order: Number(dialog.running_order) || 0,
    }
    let id = dialog.id
    if (id) await visionAPI.updateEntry(id, payload)
    else {
      const { data } = await visionAPI.createEntry(showId, payload)
      id = data.id
    }
    if (dialog.audioFile) await visionAPI.uploadEntryAudio(id, dialog.audioFile)
    setDialog(null); await reload()
  }

  const del = async (id) => {
    if (!(await confirm('Entry verwijderen?'))) return
    await visionAPI.deleteEntry(id); await reload()
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Entries ({entries.length})</CardTitle>
        <Button onClick={openNew} size="sm" disabled={!teams.length}>
          <Plus className="h-4 w-4 mr-1" /> Nieuwe entry
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {teams.length === 0 && <p className="text-sm text-muted-foreground">Maak eerst een team aan.</p>}
        {teams.length > 0 && entries.length === 0 && <p className="text-sm text-muted-foreground">Nog geen entries.</p>}
        {entries.map(e => (
          <div key={e.id} className="p-3 rounded border border-border bg-card space-y-2">
            <div className="flex items-center gap-3">
              <div className="font-mono text-xs text-muted-foreground w-8 text-center">#{e.running_order}</div>
              <TeamMark team_icon_name={e.team_icon_name} team_icon_color={e.team_icon_color} team_logo_url={e.team_logo_url} team_name={e.team_name} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{e.title}</div>
                <div className="text-xs text-muted-foreground">
                  {e.team_name}{e.artist_label ? ` · ${e.artist_label}` : ''} · <span className="uppercase">{e.genre}</span>{e.bpm ? ` · ${e.bpm} BPM` : ''}
                </div>
              </div>
              {!e.audio_url && <Badge variant="outline" className="text-yellow-500 border-yellow-500/40">Geen audio</Badge>}
              <Button variant="ghost" size="sm" onClick={() => openEdit(e)}><Edit3 className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => del(e.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
            {e.audio_url && (
              <audio controls src={e.audio_url} className="w-full h-8" preload="none" />
            )}
          </div>
        ))}
      </CardContent>

      {dialog && (
        <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{dialog.id ? 'Entry bewerken' : 'Nieuwe entry'}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div>
                <Label>Team</Label>
                <Select value={String(dialog.team_id)} onValueChange={v => setDialog({ ...dialog, team_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Kies team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Titel</Label><Input value={dialog.title} onChange={e => setDialog({ ...dialog, title: e.target.value })} placeholder="Neon Cathedral" required /></div>
              <div><Label>Artist label (optioneel)</Label><Input value={dialog.artist_label} onChange={e => setDialog({ ...dialog, artist_label: e.target.value })} placeholder="Ferrite Choir" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Genre</Label>
                  <Select value={dialog.genre} onValueChange={v => {
                    const g = genres.find(x => x.key === v)
                    setDialog({ ...dialog, genre: v, bpm: dialog.bpm || (g?.suggestedBpm ?? '') })
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {genres.map(g => <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>BPM</Label><Input type="number" value={dialog.bpm} onChange={e => setDialog({ ...dialog, bpm: e.target.value })} placeholder="128" /></div>
              </div>
              <div><Label>Volgorde</Label><Input type="number" value={dialog.running_order} onChange={e => setDialog({ ...dialog, running_order: e.target.value })} /></div>
              <div>
                <Label>MP3 upload (max 30MB)</Label>
                <Input type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg" onChange={e => setDialog({ ...dialog, audioFile: e.target.files[0] })} />
                {dialog.existingAudio && !dialog.audioFile && (
                  <audio controls src={dialog.existingAudio} className="mt-2 w-full h-8" />
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialog(null)}>Annuleer</Button>
                <Button type="submit">{dialog.id ? 'Opslaan' : 'Aanmaken'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  )
}

/* =============================================================
   Juries tab
============================================================= */
function JuriesTab({ showId, users }) {
  const [juries, setJuries] = useState([])
  const [dialog, setDialog] = useState(null)

  const reload = async () => {
    const { data } = await visionAPI.listJuries(showId)
    setJuries(data.juries)
  }
  useEffect(() => { reload() }, [showId])

  const openNew  = () => setDialog({ name: '', spokesperson: '', user_id: '', display_order: juries.length + 1 })
  const openEdit = (j) => setDialog({
    id: j.id, name: j.name, spokesperson: j.spokesperson ?? '',
    user_id: j.user_id ? String(j.user_id) : '', display_order: j.display_order,
  })

  const save = async (e) => {
    e.preventDefault()
    const payload = {
      name: dialog.name.trim(), spokesperson: dialog.spokesperson || null,
      user_id: dialog.user_id ? Number(dialog.user_id) : null,
      display_order: Number(dialog.display_order) || 0,
    }
    if (dialog.id) await visionAPI.updateJury(dialog.id, payload)
    else await visionAPI.createJury(showId, payload)
    setDialog(null); await reload()
  }

  const del = async (id) => {
    if (!(await confirm('Jury verwijderen? Alle stemmen vervallen.'))) return
    await visionAPI.deleteJury(id); await reload()
  }

  const unlock = async (j) => {
    await visionAPI.updateJury(j.id, { is_locked: false })
    await reload()
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Jury's ({juries.length})</CardTitle>
        <Button onClick={openNew} size="sm"><Plus className="h-4 w-4 mr-1" /> Nieuwe jury</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {juries.length === 0 && <p className="text-sm text-muted-foreground">Nog geen jury's.</p>}
        {juries.map(j => (
          <div key={j.id} className="flex items-center gap-3 p-3 rounded border border-border bg-card">
            <Award className="h-5 w-5 text-yellow-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{j.name}</div>
              <div className="text-xs text-muted-foreground">
                {j.spokesperson || '—'}
                {j.user_display_name || j.user_username ? ` · logs in als: ${j.user_display_name || j.user_username}` : ' · geen user gekoppeld'}
              </div>
            </div>
            {j.is_locked && (
              <Button variant="outline" size="sm" onClick={() => unlock(j)}>
                🔒 Unlock
              </Button>
            )}
            {!j.is_locked && <Badge variant="outline" className="text-blue-400 border-blue-400/40">Open</Badge>}
            <Button variant="ghost" size="sm" onClick={() => openEdit(j)}><Edit3 className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => del(j.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        ))}
      </CardContent>

      {dialog && (
        <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{dialog.id ? 'Jury bewerken' : 'Nieuwe jury'}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div><Label>Naam</Label><Input value={dialog.name} onChange={e => setDialog({ ...dialog, name: e.target.value })} required placeholder="Kempen kenners" /></div>
              <div><Label>Spokesperson</Label><Input value={dialog.spokesperson} onChange={e => setDialog({ ...dialog, spokesperson: e.target.value })} placeholder="A. den Bear" /></div>
              <div>
                <Label>User (mag als deze jury stemmen)</Label>
                <Select value={dialog.user_id} onValueChange={v => setDialog({ ...dialog, user_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Geen user gekoppeld" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">— geen —</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.display_name || u.username} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Volgorde</Label><Input type="number" value={dialog.display_order} onChange={e => setDialog({ ...dialog, display_order: e.target.value })} /></div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialog(null)}>Annuleer</Button>
                <Button type="submit">{dialog.id ? 'Opslaan' : 'Aanmaken'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  )
}

/* =============================================================
   Submissions tab — approve/reject deelnemer-inzendingen
============================================================= */
function SubmissionsTab({ showId }) {
  const [subs, setSubs] = useState([])
  const [busy, setBusy] = useState(null) // submission id being processed
  const [rejectFor, setRejectFor] = useState(null) // { id, notes }
  const [error, setError] = useState(null)

  const reload = async () => {
    try { const { data } = await visionAPI.listAdminSubmissions(); setSubs(data.submissions ?? []) }
    catch (e) { setError(e.message) }
  }
  useEffect(() => { reload() }, [showId])

  const approve = async (id) => {
    setBusy(id); setError(null)
    try { await visionAPI.approveSubmission(id); await reload() }
    catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setBusy(null) }
  }
  const reject = async () => {
    if (!rejectFor) return
    setBusy(rejectFor.id); setError(null)
    try { await visionAPI.rejectSubmission(rejectFor.id, rejectFor.notes); setRejectFor(null); await reload() }
    catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setBusy(null) }
  }

  const pending   = subs.filter(s => s.status === 'pending')
  const approved  = subs.filter(s => s.status === 'approved')
  const rejected  = subs.filter(s => s.status === 'rejected')
  const withdrawn = subs.filter(s => s.status === 'withdrawn')

  const withdraw = async (id) => {
    if (!(await confirm('Deze inzending opgeven namens de deelnemer? Als goedgekeurd, wordt de entry ook uit de show gehaald.'))) return
    setBusy(id); setError(null)
    try { await visionAPI.adminWithdrawSubmission(id); await reload() }
    catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setBusy(null) }
  }

  return (
    <div className="space-y-4">
      {error && <Card className="border-red-500/40 bg-red-500/5"><CardContent className="p-3 text-sm text-red-500">⚠ {error}</CardContent></Card>}

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Nog te reviewen ({pending.length})</CardTitle>
          <div className="text-xs text-muted-foreground">
            Files staan op <code>/uploads/vision/submissions/</code>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {pending.length === 0 && <p className="text-sm text-muted-foreground">Geen open inzendingen.</p>}
          {pending.map(s => <SubmissionRow key={s.id} sub={s} onApprove={approve} onReject={() => setRejectFor({ id: s.id, notes: '' })} onWithdraw={withdraw} busy={busy === s.id} />)}
        </CardContent>
      </Card>

      {approved.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base text-emerald-400">Goedgekeurd ({approved.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {approved.map(s => <SubmissionRow key={s.id} sub={s} onWithdraw={withdraw} busy={busy === s.id} compact />)}
          </CardContent>
        </Card>
      )}

      {rejected.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base text-red-400">Afgekeurd ({rejected.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {rejected.map(s => <SubmissionRow key={s.id} sub={s} compact showNotes />)}
          </CardContent>
        </Card>
      )}

      {withdrawn.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base text-slate-400">Opgegeven ({withdrawn.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {withdrawn.map(s => <SubmissionRow key={s.id} sub={s} compact />)}
          </CardContent>
        </Card>
      )}

      {rejectFor && (
        <Dialog open onOpenChange={(o) => !o && setRejectFor(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Inzending afkeuren</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Label>Reden (optioneel, wordt aan de deelnemer getoond)</Label>
              <Input value={rejectFor.notes} onChange={e => setRejectFor({ ...rejectFor, notes: e.target.value })}
                     placeholder="Bijvoorbeeld: opnieuw insturen met betere kwaliteit" />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRejectFor(null)}>Annuleer</Button>
              <Button onClick={reject} disabled={busy}>Afkeuren</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function SubmissionRow({ sub, onApprove, onReject, onWithdraw, busy, compact, showNotes }) {
  const flag = parseFlag(sub.team_flag_colors) ?? parseFlag(sub.proposed_team_colors)
  const teamName = sub.team_name ?? sub.proposed_team_name
  const isNewTeam = !sub.team_id
  const hasAudio = !!sub.audio_url
  return (
    <div className={`p-3 rounded border border-border bg-card ${compact ? 'py-2' : ''}`}>
      <div className="flex items-start gap-3">
        <TeamMark team_icon_name={sub.team_icon_name} team_icon_color={sub.team_icon_color} team_logo_url={sub.team_logo_url} team_name={sub.team_name || sub.proposed_team_name} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{sub.title}</span>
            {isNewTeam && sub.status === 'pending' && <Badge variant="outline" className="text-yellow-500 border-yellow-500/40 text-xs">Nieuw team</Badge>}
            {!hasAudio && sub.status !== 'withdrawn' && <Badge variant="outline" className="text-yellow-500 border-yellow-500/40 text-xs">Geen MP3</Badge>}
          </div>
          <div className="text-xs text-muted-foreground">
            <b>{teamName ?? '—'}</b>
            {sub.artist_label ? ` · ${sub.artist_label}` : ''} · <span className="uppercase">{sub.genre}</span>
            {sub.bpm ? ` · ${sub.bpm} BPM` : ''}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Door: {sub.display_name ?? sub.username} · {new Date(sub.submitted_at).toLocaleString('nl-BE')}
            {sub.withdrawn_at && ` · opgegeven ${new Date(sub.withdrawn_at).toLocaleDateString('nl-BE')}`}
          </div>
          {!compact && sub.audio_url && (
            <audio controls src={sub.audio_url} className="mt-2 w-full h-8" preload="none" />
          )}
          {showNotes && sub.admin_notes && (
            <div className="mt-2 text-xs p-2 rounded bg-red-500/10 text-red-400 border border-red-500/40">
              <b>Reden:</b> {sub.admin_notes}
            </div>
          )}
        </div>
        {onApprove && (
          <div className="flex flex-col gap-1 shrink-0">
            <Button size="sm" onClick={() => onApprove(sub.id)} disabled={busy || !hasAudio}
                    className="bg-emerald-500 hover:bg-emerald-600 text-black"
                    title={!hasAudio ? 'Deelnemer moet eerst MP3 uploaden' : ''}>
              <Check className="h-4 w-4 mr-1" />Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => onReject(sub.id)} disabled={busy}>
              <XIcon className="h-4 w-4 mr-1" />Reject
            </Button>
          </div>
        )}
        {onWithdraw && !onApprove && (
          <Button size="sm" variant="ghost" onClick={() => onWithdraw(sub.id)} disabled={busy}
                  className="text-slate-400 shrink-0" title="Opgeven namens deelnemer">
            <XIcon className="h-4 w-4" />
          </Button>
        )}
        {onWithdraw && onApprove && (
          <Button size="sm" variant="ghost" onClick={() => onWithdraw(sub.id)} disabled={busy}
                  className="text-slate-400 shrink-0 text-xs" title="Opgeven namens deelnemer">
            opgeven
          </Button>
        )}
      </div>
    </div>
  )
}

/* =============================================================
   Admin jury voting tab — admin voert punten in namens jury
============================================================= */
const NO_POINTS = '__none__'
const VOTE_STEPS = [1,2,3,4,5,6,7,8,10,12]

function AdminJuryVotingTab({ showId }) {
  const [juries, setJuries] = useState([])
  const [selectedJuryId, setSelectedJuryId] = useState(null)
  const [data, setData] = useState(null)
  const [votes, setVotes] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  const loadJuries = async () => {
    try { const r = await visionAPI.listJuries(showId); setJuries(r.data.juries || []) }
    catch (e) { setError(e.message) }
  }
  useEffect(() => { loadJuries() }, [showId])

  const loadJuryData = async (id) => {
    setError(null); setSaved(false)
    try {
      const r = await visionAPI.adminGetJuryData(id)
      setData(r.data)
      const v = {}
      r.data.votes?.forEach(x => { v[x.entry_id] = Number(x.points) })
      setVotes(v)
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }

  const pickJury = (id) => {
    setSelectedJuryId(id ? Number(id) : null)
    if (id) loadJuryData(Number(id))
    else { setData(null); setVotes({}) }
  }

  const usedPoints = new Set(Object.entries(votes).filter(([, p]) => p != null).map(([, p]) => p))

  const changePoints = (entryId, val) => {
    setSaved(false); setError(null)
    setVotes(prev => {
      const next = { ...prev }
      if (val === NO_POINTS || val === '' || val == null) delete next[entryId]
      else next[entryId] = Number(val)
      return next
    })
  }

  const payload = () => Object.entries(votes)
    .filter(([, p]) => p != null)
    .map(([entry_id, points]) => ({ entry_id: Number(entry_id), points }))

  const save = async () => {
    if (!selectedJuryId) return
    setSaving(true); setError(null)
    try { await visionAPI.adminSaveJuryVotes(selectedJuryId, payload()); setSaved(true) }
    catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setSaving(false) }
  }

  const submit = async () => {
    if (!selectedJuryId) return
    if (!(await confirm('Definitief indienen? Jury wordt gelockt. Kan later ge-unlockt worden.'))) return
    setSaving(true); setError(null)
    try {
      await visionAPI.adminSaveJuryVotes(selectedJuryId, payload())
      await visionAPI.adminSubmitJury(selectedJuryId)
      await loadJuryData(selectedJuryId)
      await loadJuries()
    } catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setSaving(false) }
  }

  const unlock = async () => {
    await visionAPI.updateJury(selectedJuryId, { is_locked: false })
    await loadJuryData(selectedJuryId); await loadJuries()
  }

  const locked = data?.jury?.is_locked

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Punten invoeren namens jury</CardTitle>
          <p className="text-xs text-muted-foreground">Gebruik dit tijdens de pauze tussen live songs en jury reveal. Jury's zonder eigen pc → admin tikt hun punten in.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Kies jury</Label>
            <Select value={selectedJuryId ? String(selectedJuryId) : ''} onValueChange={pickJury}>
              <SelectTrigger><SelectValue placeholder="— kies jury —" /></SelectTrigger>
              <SelectContent>
                {juries.map(j => (
                  <SelectItem key={j.id} value={String(j.id)}>
                    #{j.display_order} · {j.name}{j.spokesperson ? ` (${j.spokesperson})` : ''}{j.is_locked ? ' ✓ ingediend' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {!data && selectedJuryId && <div className="text-sm text-muted-foreground">Laden…</div>}

      {data && (
        <>
          {locked && (
            <Card className="border-emerald-500/40 bg-emerald-500/5">
              <CardContent className="p-3 flex items-center gap-3">
                <Lock className="h-5 w-5 text-emerald-400" />
                <div className="flex-1">
                  <div className="font-semibold text-emerald-400">Ingediend</div>
                  <div className="text-xs text-muted-foreground">Punten zijn gelockt. Unlock om te wijzigen.</div>
                </div>
                <Button size="sm" variant="outline" onClick={unlock}>Unlock</Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="h-4 w-4 text-yellow-500" /> {data.jury.name}
              </CardTitle>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {VOTE_STEPS.slice().reverse().map(p => (
                  <Badge key={p} variant={usedPoints.has(p) ? 'default' : 'outline'}
                         className={usedPoints.has(p) ? 'bg-yellow-500 text-black' : 'text-muted-foreground'}>
                    {p}
                  </Badge>
                ))}
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {data.entries.length === 0 && <p className="text-sm text-muted-foreground">Geen entries in deze show.</p>}
              {data.entries.map(e => {
                const current = votes[e.id] ?? null
                return (
                  <div key={e.id} className="flex items-center gap-3 p-2 rounded border border-border">
                    <span className="font-mono text-xs text-muted-foreground w-6 text-center shrink-0">#{e.running_order}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{e.title}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {e.team_name}{e.artist_label ? ` · ${e.artist_label}` : ''} · <span className="uppercase">{e.genre}</span>
                      </div>
                    </div>
                    <div className="w-36 shrink-0">
                      <Select
                        value={current == null ? NO_POINTS : String(current)}
                        onValueChange={(v) => changePoints(e.id, v)}
                        disabled={locked}
                      >
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_POINTS}>— geen —</SelectItem>
                          {VOTE_STEPS.slice().reverse().map(p => {
                            const disabled = usedPoints.has(p) && p !== current
                            return (
                              <SelectItem key={p} value={String(p)} disabled={disabled}>
                                {p} punten{disabled ? ' (bezet)' : ''}
                              </SelectItem>
                            )
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>

          {error && <Card className="border-red-500/40 bg-red-500/5"><CardContent className="p-3 text-sm text-red-500">⚠ {error}</CardContent></Card>}
          {saved && !error && <div className="text-sm text-emerald-400">✓ Opgeslagen als concept — nog niet definitief</div>}

          {!locked && (
            <div className="flex gap-2 justify-end sticky bottom-0 bg-background/95 backdrop-blur p-3 rounded-lg border border-border">
              <div className="flex-1 text-sm self-center text-muted-foreground">
                {usedPoints.size} van {VOTE_STEPS.length} punt-waardes toegekend
              </div>
              <Button variant="secondary" onClick={save} disabled={saving}>
                {saving ? 'Opslaan…' : 'Opslaan (concept)'}
              </Button>
              <Button onClick={submit} disabled={saving}>
                {saving ? 'Bezig…' : 'Definitief indienen'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* =============================================================
   Publiek scoring tab — admin voert totaal punten in per entry
   (via dB meter meting of ander applaus-mechanisme)
============================================================= */
function AdminPublicScoringTab() {
  const [data, setData] = useState(null)
  const [scores, setScores] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  const load = async () => {
    setError(null); setSaved(false)
    try {
      const r = await visionAPI.adminGetPublicScores()
      setData(r.data)
      const s = {}
      r.data.entries?.forEach(e => { s[e.entry_id] = e.admin_score })
      setScores(s)
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
  }
  useEffect(() => { load() }, [])

  const save = async () => {
    setSaving(true); setError(null); setSaved(false)
    try {
      const list = Object.entries(scores).map(([entry_id, points]) => ({
        entry_id: Number(entry_id), points: Number(points) || 0,
      }))
      await visionAPI.adminSavePublicScores(list)
      setSaved(true)
      await load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) }
    finally { setSaving(false) }
  }

  const setScore = (entryId, v) => {
    setSaved(false); setError(null)
    setScores(prev => ({ ...prev, [entryId]: v === '' ? 0 : Math.max(0, Number(v) || 0) }))
  }

  if (!data) return <div className="text-muted-foreground text-sm">Laden…</div>
  if (!data.show) return <div className="text-muted-foreground text-sm">Geen actieve show</div>

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Publiek punten invoeren</CardTitle>
          <p className="text-xs text-muted-foreground">
            Voer per entry de totale publiekstemmen in. Handig als je bv met een dB meter applaus meet.
            Individuele stemmen van users (via /kempenvision/vote) worden apart bijgeteld in de standings.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.entries.length === 0 && <p className="text-sm text-muted-foreground">Geen entries.</p>}
          {data.entries.map(e => (
            <div key={e.entry_id} className="flex items-center gap-3 p-2 rounded border border-border">
              <span className="font-mono text-xs text-muted-foreground w-6 text-center shrink-0">#{e.running_order}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{e.title}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {e.team_name}
                  {e.user_total > 0 && <span className="ml-2 text-cyan-400">+{e.user_total} van users</span>}
                </div>
              </div>
              <Input
                type="number" min="0"
                value={scores[e.entry_id] ?? 0}
                onChange={ev => setScore(e.entry_id, ev.target.value)}
                className="w-24 tabular-nums text-right"
                placeholder="0"
              />
              <span className="text-xs text-muted-foreground w-8">pt</span>
            </div>
          ))}
        </CardContent>
      </Card>
      {error && <Card className="border-red-500/40 bg-red-500/5"><CardContent className="p-3 text-sm text-red-500">⚠ {error}</CardContent></Card>}
      {saved && !error && <div className="text-sm text-emerald-400">✓ Opgeslagen — standings updaten automatisch</div>}
      <div className="flex justify-end sticky bottom-0 bg-background/95 backdrop-blur p-3 rounded-lg border border-border">
        <Button onClick={save} disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button>
      </div>
    </div>
  )
}

/* Verplichte Uitreiking-tab — één plek waar de admin de hele show afsluit.
   Toont status, uitleg, en de knop. Badge-configuratie zit nog in Show-tab
   (kan later hierheen mee). */
function UitreikingTab({ show, onDone }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const doAward = async () => {
    setBusy(true); setMsg('')
    try {
      const r = await visionAPI.awardResults(show.id)
      setMsg(`Uitreiking uitgevoerd — ${r.data?.awarded ?? ''} spelers kregen punten/badges.`)
      onDone?.()
    } catch (e) { setMsg(`Fout: ${e.response?.data?.error ?? e.message}`) }
    finally { setBusy(false) }
  }
  const done = !!show.results_awarded_at
  return (
    <div className="space-y-4 max-w-2xl">
      <Card style={{ borderLeft: `3px solid ${done ? '#61E294' : '#FFB57A'}` }}>
        <CardContent className="p-5 space-y-3">
          <div className="font-semibold text-lg">
            {done
              ? <span className="text-emerald-400">✓ Uitgereikt</span>
              : <span className="text-peach">Nog niet uitgereikt</span>}
          </div>
          <p className="text-sm text-muted-foreground">
            {done
              ? `Gebeurd op ${new Date(show.results_awarded_at).toLocaleString('nl-BE')}. Top-5 staat nu in het compo-overzicht en de winnaar heeft de badge (indien geconfigureerd).`
              : 'Registreert top-5 in het compo-overzicht en reikt de winnaar-badge uit aan de 1e plaats (user-mode = die user, team-mode = alle vaste teamleden). Winnaar-badge configureer je in de Show-tab.'}
          </p>
          <Button size="lg" onClick={doAward} disabled={busy || done}>
            <Award className="h-4 w-4 mr-2" />
            {busy ? 'Bezig…' : done ? 'Al uitgereikt' : 'Uitreiken'}
          </Button>
          {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
        </CardContent>
      </Card>
    </div>
  )
}
